import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "submitBtn", "messages", "conversation", "welcome", "typingTemplate", "errorTemplate", "scrollBtn", "exportSection", "exportToggle", "exportForm", "exportEmail", "exportFirstName", "exportHoneypot", "exportSubmitBtn", "historyOverlay", "historyDrawer", "historyList", "historyItemTemplate", "historyEmptyTemplate", "userMessageTemplate"]
  static values = {
    sessionId: String,
    chatUrl: { type: String, default: "/ai/chat" },
    renderUrl: { type: String, default: "/ai/assistant/render_message" },
    restoreUrl: { type: String, default: "/ai/conversations" },
    exportUrl: { type: String, default: "/ai/conversation_exports" },
    initialQuery: { type: String, default: "" }
  }

  static PLACEHOLDERS = [
    "Posez votre question sur les SCPI...",
    "Quel rendement attendre d'une SCPI ?",
    "Quelle fiscalite pour les SCPI europeennes ?",
    "Comment diversifier avec les SCPI ?",
    "Quels frais pour investir en SCPI ?",
    "SCPI en assurance-vie ou en direct ?"
  ]

  connect() {
    this.history = []
    this.isStreaming = false
    this.messageCounter = 0
    this.userHasScrolledUp = false
    this.placeholderIndex = 0

    this._boundHandleScroll = this._handleScroll.bind(this)
    this._boundRotatePlaceholder = this._rotatePlaceholder.bind(this)

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("focus", this._boundRotatePlaceholder)
    }

    this._initConversation()
  }

  async _initConversation() {
    if (this.initialQueryValue) {
      // Arriving from another page (search bar) → new session, ignore previous history
      this.history = []
      this.sessionIdValue = crypto.randomUUID()
      this._trackSessionId()
      this.sendMessage(this.initialQueryValue)
      this._clearQueryParam()
    } else {
      // Staying on /assistant or returning → restore previous conversation
      await this._restoreFromServer()
    }
  }

  disconnect() {
    if (this.hasConversationTarget) {
      this.conversationTarget.removeEventListener("scroll", this._boundHandleScroll)
    }
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("focus", this._boundRotatePlaceholder)
    }
  }

  submit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (query === "" || this.isStreaming) return

    this.sendMessage(query)
    this.inputTarget.value = ""
    this.resetTextareaHeight()
  }

  handleEnter(event) {
    if (event.shiftKey) return
    event.preventDefault()
    this.submit(event)
  }

  askSuggestion(event) {
    const suggestion = event.currentTarget.dataset.suggestion
    if (!suggestion || this.isStreaming) return
    this.sendMessage(suggestion)
  }

  toggleExportForm() {
    if (this.hasExportFormTarget) {
      this.exportFormTarget.classList.toggle("hidden")
    }
    if (this.hasExportToggleTarget) {
      this.exportToggleTarget.classList.toggle("hidden")
    }
  }

  async submitExport(event) {
    event.preventDefault()

    const email = this.exportEmailTarget.value.trim()
    if (!email) return

    const firstName = this.hasExportFirstNameTarget ? this.exportFirstNameTarget.value.trim() : ""

    this.exportSubmitBtnTarget.disabled = true
    this.exportSubmitBtnTarget.textContent = "Envoi..."

    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          email: email,
          first_name: firstName,
          website: this.hasExportHoneypotTarget ? this.exportHoneypotTarget.value : "",
          messages: this.history
        })
      })

      const html = await response.text()
      window.Turbo.renderStreamMessage(html)

      // Hide form, show toggle with success state
      this.exportFormTarget.classList.add("hidden")
      this.exportToggleTarget.classList.remove("hidden")
    } catch (error) {
      console.error("[ai-chat] Export error:", error)
    } finally {
      this.exportSubmitBtnTarget.disabled = false
      this.exportSubmitBtnTarget.textContent = "Envoyer"
    }
  }

  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 150) + "px"
  }

  copyMessage(event) {
    const btn = event.currentTarget
    const messageId = btn.dataset.messageId
    const messageEl = document.getElementById(messageId)
    if (!messageEl) return

    const textEl = messageEl.querySelector(".ai-message__text")
    if (!textEl) return

    const text = textEl.innerText
    navigator.clipboard.writeText(text).then(() => {
      const labelSpan = btn.querySelector("span")
      const originalText = labelSpan.textContent
      labelSpan.textContent = "Copie !"
      btn.classList.add("ai-copy-btn--copied")

      setTimeout(() => {
        labelSpan.textContent = originalText
        btn.classList.remove("ai-copy-btn--copied")
      }, 2000)
    })
  }

  retryLast() {
    if (this.isStreaming || this.history.length === 0) return

    const lastUserMessage = [...this.history].reverse().find(m => m.role === "user")
    if (!lastUserMessage) return

    // Remove error element from DOM
    const errorEl = this.messagesTarget.querySelector(".ai-error-message")
    if (errorEl) {
      const parentMsg = errorEl.closest(".ai-message")
      if (parentMsg) parentMsg.remove()
    }

    // Remove last user message from history to avoid duplicate
    const lastUserIdx = this.history.lastIndexOf(lastUserMessage)
    if (lastUserIdx !== -1) {
      this.history.splice(lastUserIdx, 1)
    }

    this.sendMessage(lastUserMessage.content)
  }

  scrollToBottomAction() {
    this.scrollToBottom()
  }

  async openHistory() {
    if (this.hasHistoryOverlayTarget && this.hasHistoryDrawerTarget) {
      this.historyOverlayTarget.classList.remove("hidden")
      this.historyDrawerTarget.classList.add("open")
      await this._loadHistoryList()
    }
  }

  closeHistory() {
    if (this.hasHistoryOverlayTarget && this.hasHistoryDrawerTarget) {
      this.historyDrawerTarget.classList.remove("open")
      this.historyOverlayTarget.classList.add("hidden")
    }
  }

  async loadConversation(event) {
    const sessionId = event.currentTarget.dataset.sessionId
    if (!sessionId || sessionId === this.sessionIdValue) {
      this.closeHistory()
      return
    }

    // Activate cookie server-side
    await fetch(`${this.restoreUrlValue}/${sessionId}/activate`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfToken }
    })

    // Update local state and reload conversation
    this.sessionIdValue = sessionId
    this.history = []
    this.messageCounter = 0
    if (this.hasMessagesTarget) this.messagesTarget.replaceChildren()

    this.closeHistory()
    await this._restoreFromServer()
  }

  async deleteConversation(event) {
    event.stopPropagation()
    const sessionId = event.currentTarget.dataset.sessionId
    if (!sessionId) return

    if (!confirm("Supprimer cette conversation ? Elle ne sera plus accessible.")) return

    try {
      await fetch(`${this.restoreUrlValue}/${sessionId}/request_deletion`, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this.csrfToken }
      })

      // Remove from localStorage
      const ids = this._getTrackedSessionIds().filter(id => id !== sessionId)
      localStorage.setItem("ai_session_ids", JSON.stringify(ids))

      // If deleting current conversation, reset UI
      if (sessionId === this.sessionIdValue) {
        await this.newConversation()
      } else {
        await this._loadHistoryList()
      }
    } catch (e) {
      // Ignore errors silently
    }
  }

  async newConversation() {
    const newSessionId = crypto.randomUUID()

    // Activate cookie server-side
    await fetch(`${this.restoreUrlValue}/${newSessionId}/activate`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfToken }
    })

    this._trackSessionId(newSessionId)
    this.sessionIdValue = newSessionId
    this.history = []
    this.messageCounter = 0

    if (this.hasMessagesTarget) this.messagesTarget.replaceChildren()
    if (this.hasConversationTarget) this.conversationTarget.classList.add("hidden")
    if (this.hasWelcomeTarget) this.welcomeTarget.classList.remove("hidden")
    if (this.hasExportSectionTarget) this.exportSectionTarget.classList.add("hidden")

    this.closeHistory()
    this.inputTarget.focus()
  }

  // --- Private ---

  async sendMessage(query) {
    this.isStreaming = true
    this.showConversation()
    this.disableInput()

    // Append user message
    const userMsgId = this.nextMessageId()
    this.appendUserMessage(query, userMsgId)

    // Append typing indicator from template
    const assistantMsgId = this.nextMessageId()
    this.appendTypingIndicator(assistantMsgId)

    // Scroll to bottom
    this.scrollToBottom()

    // Add user message to history
    this.history.push({ role: "user", content: query })

    // Track session_id on first message
    if (this.history.length === 1) {
      this._trackSessionId()
    }

    try {
      await this.streamResponse(query, userMsgId, assistantMsgId)
    } catch (error) {
      this.handleStreamError(assistantMsgId, error)
    } finally {
      this.isStreaming = false
      this.enableInput()
      this.inputTarget.focus()
    }
  }

  async streamResponse(query, userMsgId, assistantMsgId) {
    const historyToSend = this.history.slice(0, -1)

    const response = await fetch(this.chatUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        message: query,
        session_id: this.sessionIdValue,
        history: historyToSend
      })
    })

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let streamedText = ""
    let buffer = ""
    let currentEvent = "message"

    const typingEl = document.getElementById(assistantMsgId)
    const textEl = typingEl?.querySelector(".ai-message__text")

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop()

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim()
          continue
        }

        if (line.startsWith("data: ")) {
          const jsonStr = line.slice(6)
          try {
            const data = JSON.parse(jsonStr)

            if (currentEvent === "new_session") {
              // Clear old messages from DOM, keep only current user msg + typing
              const currentUserMsg = document.getElementById(userMsgId)
              const currentTyping = document.getElementById(assistantMsgId)
              this.messagesTarget.replaceChildren()
              if (currentUserMsg) this.messagesTarget.appendChild(currentUserMsg)
              if (currentTyping) this.messagesTarget.appendChild(currentTyping)

              this.sessionIdValue = data.session_id
              this.history = [{ role: "user", content: query }]
              this.messageCounter = 2
              this._trackSessionId(data.session_id)
              this._activateSession(data.session_id)
              currentEvent = "message"
              continue
            }

            if (currentEvent === "error") {
              this.handleStreamError(assistantMsgId, new Error(data.error))
              return
            }

            if (currentEvent === "done" && data.content) {
              await this.replaceWithRendered(assistantMsgId, data.content)
              this.history.push({ role: "assistant", content: data.content })
              return
            }

            if (currentEvent === "message" && data.content) {
              streamedText += data.content
              if (textEl) {
                textEl.textContent = streamedText
              }
              this.scrollToBottom()
            }
          } catch (e) {
            // Ignore malformed JSON
          }
          currentEvent = "message"
        }
      }
    }

    // Fallback if stream ends without done event
    if (streamedText) {
      await this.replaceWithRendered(assistantMsgId, streamedText)
      this.history.push({ role: "assistant", content: streamedText })
    }
  }

  async replaceWithRendered(messageId, content) {
    const response = await fetch(this.renderUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken
      },
      body: new URLSearchParams({
        body: content,
        role: "assistant",
        message_id: messageId
      })
    })

    const html = await response.text()
    window.Turbo.renderStreamMessage(html)
    this.scrollToBottom()
  }

  appendUserMessage(query, msgId) {
    const fragment = this.userMessageTemplateTarget.content.cloneNode(true)
    const wrapper = fragment.querySelector(".ai-message")
    wrapper.id = msgId
    fragment.querySelector("[data-user-text]").textContent = query

    this.messagesTarget.appendChild(fragment)
  }

  appendTypingIndicator(msgId) {
    const template = this.typingTemplateTarget.content.cloneNode(true)
    const wrapper = template.querySelector(".ai-message")
    wrapper.id = msgId

    this.messagesTarget.appendChild(template)
  }

  handleStreamError(assistantMsgId, error) {
    const el = document.getElementById(assistantMsgId)
    if (!el) return

    const contentEl = el.querySelector(".ai-message__content")
    if (!contentEl) return

    const errorFragment = this.errorTemplateTarget.content.cloneNode(true)
    const textEl = errorFragment.querySelector("[data-error-text]")
    if (textEl) {
      textEl.textContent = error.message || "Une erreur est survenue. Veuillez reessayer."
    }

    contentEl.replaceChildren(errorFragment)
  }

  showConversation() {
    if (this.hasWelcomeTarget) {
      this.welcomeTarget.classList.add("hidden")
    }
    if (this.hasConversationTarget) {
      this.conversationTarget.classList.remove("hidden")
      this.conversationTarget.addEventListener("scroll", this._boundHandleScroll)
    }
    if (this.hasExportSectionTarget) {
      this.exportSectionTarget.classList.remove("hidden")
    }
  }

  disableInput() {
    this.submitBtnTarget.disabled = true
    this.inputTarget.disabled = true
  }

  enableInput() {
    this.submitBtnTarget.disabled = false
    this.inputTarget.disabled = false
  }

  resetTextareaHeight() {
    this.inputTarget.style.height = "auto"
  }

  scrollToBottom() {
    if (this.hasConversationTarget) {
      this.conversationTarget.scrollTo({
        top: this.conversationTarget.scrollHeight,
        behavior: "smooth"
      })
    }
  }

  _handleScroll() {
    if (!this.hasConversationTarget || !this.hasScrollBtnTarget) return

    const el = this.conversationTarget
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight

    if (distanceFromBottom > 100) {
      this.scrollBtnTarget.classList.remove("hidden")
    } else {
      this.scrollBtnTarget.classList.add("hidden")
    }
  }

  async _restoreFromServer() {
    try {
      const url = `${this.restoreUrlValue}/${this.sessionIdValue}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) return false

      const data = await response.json()
      if (!data.history || data.history.length === 0) return false

      this.history = data.history
      this.messageCounter = data.message_count || data.history.length

      this.showConversation()
      if (this.hasMessagesTarget && data.messages_html) {
        this.messagesTarget.replaceChildren()
        this.messagesTarget.insertAdjacentHTML("beforeend", data.messages_html)
      }

      // Delay scroll to ensure layout is complete after HTML insertion
      setTimeout(() => {
        this.scrollToBottom()
      }, 100)
      return true
    } catch (e) {
      return false
    }
  }

  _trackSessionId(sessionId = null) {
    const id = sessionId || this.sessionIdValue
    try {
      const ids = this._getTrackedSessionIds()
      if (!ids.includes(id)) {
        ids.unshift(id)
        localStorage.setItem("ai_session_ids", JSON.stringify(ids.slice(0, 50)))
      }
    } catch (e) {
      // localStorage unavailable
    }
  }

  _activateSession(sessionId) {
    fetch(`${this.restoreUrlValue}/${sessionId}/activate`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfToken }
    }).catch(() => {})
  }

  _getTrackedSessionIds() {
    try {
      const raw = localStorage.getItem("ai_session_ids")
      return raw ? JSON.parse(raw) : []
    } catch (e) {
      return []
    }
  }

  async _loadHistoryList() {
    if (!this.hasHistoryListTarget) return

    const sessionIds = this._getTrackedSessionIds()
    if (sessionIds.length === 0) {
      this._showHistoryEmpty("Aucune conversation")
      return
    }

    this._showHistoryEmpty("Chargement...")

    try {
      const params = new URLSearchParams()
      sessionIds.forEach(id => params.append("session_ids[]", id))

      const response = await fetch(`${this.restoreUrlValue}?${params}`, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) throw new Error("Failed to load history")

      const conversations = await response.json()

      if (conversations.length === 0) {
        this._showHistoryEmpty("Aucune conversation")
        return
      }

      this.historyListTarget.replaceChildren()

      conversations.forEach(conv => {
        const item = this._buildHistoryItem(conv)
        this.historyListTarget.appendChild(item)
      })
    } catch (e) {
      this._showHistoryEmpty("Erreur de chargement")
    }
  }

  _showHistoryEmpty(text) {
    this.historyListTarget.replaceChildren()
    const fragment = this.historyEmptyTemplateTarget.content.cloneNode(true)
    fragment.querySelector("[data-history-empty-text]").textContent = text
    this.historyListTarget.appendChild(fragment)
  }

  _buildHistoryItem(conv) {
    const fragment = this.historyItemTemplateTarget.content.cloneNode(true)
    const item = fragment.querySelector(".ai-history-item")

    item.dataset.sessionId = conv.session_id
    if (conv.session_id === this.sessionIdValue) {
      item.classList.add("active")
    }

    const deleteBtn = item.querySelector(".ai-history-delete-btn")
    deleteBtn.dataset.sessionId = conv.session_id

    fragment.querySelector("[data-history-message]").textContent =
      conv.first_message || "Conversation sans titre"

    const date = new Date(conv.updated_at)
    const dateStr = date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
    const timeStr = date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })

    fragment.querySelector("[data-history-date]").textContent = `${dateStr} ${timeStr}`
    fragment.querySelector("[data-history-count]").textContent = `${conv.message_count} msg`

    return fragment
  }

  _clearQueryParam() {
    const url = new URL(window.location)
    if (url.searchParams.has("q")) {
      url.searchParams.delete("q")
      window.history.replaceState({}, "", url.pathname + url.search)
    }
  }

  _rotatePlaceholder() {
    this.placeholderIndex = (this.placeholderIndex + 1) % this.constructor.PLACEHOLDERS.length
    this.inputTarget.placeholder = this.constructor.PLACEHOLDERS[this.placeholderIndex]
  }

  nextMessageId() {
    this.messageCounter++
    return `ai-msg-${this.messageCounter}`
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
